import { useState, useEffect, useCallback } from 'react';
import { fetchFacturacion, fetchProperties, updatePaymentAbono, uploadInvoiceXml, uploadInvoicePdf, downloadInvoiceFile } from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, DollarSign, CheckCircle2, Clock, AlertTriangle, Pencil, Upload, Download, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const INVOICE_TYPES = { anticipo: 'Anticipo', parcialidad: 'Parcialidad', complemento: 'Complemento' };
const PAY_STATUS = {
  received: { label: 'Completo', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { label: 'Parcial', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export default function Facturacion() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [filterProp, setFilterProp] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [properties, setProperties] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('');

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : d === 'desc' ? '' : 'asc');
      if (sortDir === 'desc') setSortCol('');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortedItems = [...(data?.items || [])].sort((a, b) => {
    if (!sortCol || !sortDir) return 0;
    let va = a[sortCol] ?? '';
    let vb = b[sortCol] ?? '';
    if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
    va = String(va).toLowerCase();
    vb = String(vb).toLowerCase();
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.invoiced = filter;
      if (filterProp !== 'all') params.property_id = filterProp;
      if (filterStatus !== 'all') params.status = filterStatus;
      const [d, p] = await Promise.all([fetchFacturacion(params), fetchProperties()]);
      setData(d);
      setProperties(p);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [filter, filterProp, filterStatus]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[400px] rounded-xl" /></div>;

  const k = data?.kpis || {};
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="facturacion-page">
      <PageHeader title="Facturación" subtitle="Control de pagos cobrados y facturas" />

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPICard label="Total Cobrado" value={formatMXNCompact(k.total_cobrado)} icon={DollarSign} compact />
        <KPICard label="Facturado" value={formatMXNCompact(k.total_facturado)} icon={CheckCircle2} className="border-emerald-200" compact />
        <KPICard label="Pendiente Facturar" value={formatMXNCompact(k.pendiente_facturar)} icon={AlertTriangle} className={k.pendiente_facturar > 0 ? 'border-amber-200' : ''} compact />
        <KPICard label="Completos" value={k.count_complete} icon={CheckCircle2} compact />
        <KPICard label="Parciales" value={k.count_partial} icon={Clock} compact />
        <KPICard label="Sin Factura" value={k.count_pending} icon={FileText} compact />
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="yes">Facturados</SelectItem>
            <SelectItem value="no">Sin facturar</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo estatus</SelectItem>
            <SelectItem value="received">Completos</SelectItem>
            <SelectItem value="partial">Parciales</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterProp} onValueChange={setFilterProp}>
          <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las viviendas</SelectItem>
            {properties.map(p => <SelectItem key={p.id} value={p.id}>Lt {p.lot} Mz {p.block}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Sin pagos registrados</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
          <Table>
            <TableHeader className="sticky top-0 z-20">
              <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <SortHead col="vivienda" label="Vivienda" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <SortHead col="cliente" label="Cliente" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2">Tipo</TableHead>
                <SortHead col="last_payment_date" label="F. Pago" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} align="right" />
                <SortHead col="total_cobrado" label="Cobrado" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} align="right" color="text-[#F05A28]" />
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2">Est. Pago</TableHead>
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-center">Facturado</TableHead>
                <SortHead col="invoice_folio" label="Folio" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <SortHead col="invoice_date" label="F. Factura" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                <SortHead col="invoice_total" label="Total Factura" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} align="right" />
                <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2 text-center">Archivos</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map(r => {
                const st = PAY_STATUS[r.payment_status];
                return (
                  <TableRow key={r.payment_id} className="group">
                    <TableCell className="py-2 px-2 text-[12px] font-semibold whitespace-nowrap">{r.vivienda}</TableCell>
                    <TableCell className="py-2 px-2 text-[11px] text-slate-500 truncate max-w-[120px]">{r.cliente}</TableCell>
                    <TableCell className="py-2 px-2"><Badge variant="outline" className="text-[8px]">{r.payment_type === 'anticipo' ? 'Anticipo' : r.payment_type === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge></TableCell>
                    <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums">{r.last_payment_date ? formatDate(r.last_payment_date) : '—'}</TableCell>
                    <TableCell className="py-2 px-2 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(r.total_cobrado)}</TableCell>
                    <TableCell className="py-2 px-2">{st ? <Badge variant="outline" className={`text-[8px] ${st.cls}`}>{st.label}</Badge> : '—'}</TableCell>
                    <TableCell className="py-2 px-2 text-center">
                      {r.is_invoiced ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mx-auto" /> : <span className="text-slate-300 text-[10px]">—</span>}
                    </TableCell>
                    <TableCell className="py-2 px-2 text-[11px] text-slate-600">{r.invoice_folio || '—'}</TableCell>
                    <TableCell className="py-2 px-2 text-[11px] tabular-nums">{r.invoice_date ? formatDate(r.invoice_date) : '—'}</TableCell>
                    <TableCell className="py-2 px-2 text-right text-[11px] tabular-nums">{r.invoice_total ? formatMXN(r.invoice_total) : <span className="text-slate-300 text-[10px]">—</span>}</TableCell>
                    <TableCell className="py-2 px-2 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {r.invoice_pdf_path && <Button variant="ghost" size="icon" className="h-5 w-5 text-red-500" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/facturacion/file/${r.invoice_pdf_path}?token=${localStorage.getItem('quru_token')}`, '_blank')} title="Ver PDF"><Eye className="h-3 w-3" /></Button>}
                        {r.invoice_xml_path && <Button variant="ghost" size="icon" className="h-5 w-5 text-blue-500" onClick={() => downloadInvoiceFile(r.invoice_xml_path)} title="Descargar XML"><Download className="h-3 w-3" /></Button>}
                        {!r.invoice_xml_path && !r.invoice_pdf_path && <span className="text-slate-300 text-[10px]">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="py-2 px-2">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-slate-700 opacity-0 group-hover:opacity-100" onClick={() => setEditTarget(r)}><Pencil className="h-3 w-3" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {editTarget && <InvoiceEditDialog abono={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}

      {/* PDF Preview Modal */}
      {pdfPreview && (
        <Dialog open onOpenChange={() => setPdfPreview(null)}>
          <DialogContent className="sm:max-w-4xl h-[85vh]">
            <DialogHeader><DialogTitle>Vista Previa de Factura</DialogTitle></DialogHeader>
            <iframe
              src={`${process.env.REACT_APP_BACKEND_URL}/api/facturacion/file/${pdfPreview}?token=${localStorage.getItem('quru_token')}`}
              className="w-full flex-1 rounded-lg border"
              style={{ height: 'calc(85vh - 100px)' }}
              title="PDF Preview"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function InvoiceEditDialog({ abono, onClose, onSaved }) {
  const [form, setForm] = useState({
    is_invoiced: abono.is_invoiced || false,
    invoice_folio: abono.invoice_folio || '',
    invoice_date: abono.invoice_date || '',
    invoice_type: abono.invoice_type || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const abonoId = abono.abono_id;

  const handleXmlUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !abonoId) return;
    setUploading(true);
    try {
      const res = await uploadInvoiceXml(abonoId, file);
      toast.success(`XML procesado: Folio ${res.parsed?.folio || ''} — ${formatMXN(res.parsed?.total || 0)}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al procesar XML'); }
    finally { setUploading(false); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !abonoId) return;
    setUploading(true);
    try {
      await uploadInvoicePdf(abonoId, file);
      toast.success('PDF adjuntado');
      onSaved();
    } catch { toast.error('Error al subir PDF'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!abonoId) { toast.error('Sin abono vinculado'); return; }
    setSaving(true);
    try {
      await updatePaymentAbono(abonoId, form);
      toast.success('Actualizado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#F05A28]" /> Factura</DialogTitle>
          <p className="text-xs text-slate-400">{abono.vivienda} — {abono.cliente} — {formatMXN(abono.total_cobrado)}</p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="border-2 border-dashed border-[#F05A28]/20 rounded-lg p-4 text-center">
            <Upload className="h-8 w-8 text-[#F05A28]/40 mx-auto mb-2" />
            <p className="text-xs text-slate-600 mb-1">Sube el XML del CFDI</p>
            <p className="text-[10px] text-slate-400 mb-2">Extrae folio, UUID, monto, fecha automáticamente</p>
            <label className="inline-flex cursor-pointer">
              <input type="file" accept=".xml" className="hidden" onChange={handleXmlUpload} disabled={uploading} />
              <Button variant="outline" size="sm" disabled={uploading} asChild><span>{uploading ? 'Procesando...' : 'Subir XML'}</span></Button>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex-1 cursor-pointer">
              <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} disabled={uploading} />
              <Button variant="outline" size="sm" className="w-full text-xs" disabled={uploading} asChild><span><Upload className="h-3 w-3 mr-1.5" />{abono.invoice_pdf_path ? 'Reemplazar PDF' : 'Adjuntar PDF'}</span></Button>
            </label>
            {abono.invoice_pdf_path && (
              <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => downloadInvoiceFile(abono.invoice_pdf_path)}>
                <Download className="h-3 w-3 mr-1" /> Descargar
              </Button>
            )}
          </div>

          {abono.invoice_uuid && (
            <div className="bg-emerald-50 rounded-lg p-3 text-xs space-y-1">
              <p className="text-[9px] uppercase tracking-widest text-emerald-600 mb-1">Datos del CFDI</p>
              <div className="flex justify-between"><span className="text-slate-500">UUID</span><span className="font-mono text-[9px]">{abono.invoice_uuid}</span></div>
              {abono.invoice_total && <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold tabular-nums">{formatMXN(abono.invoice_total)}</span></div>}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox checked={form.is_invoiced} onCheckedChange={v => set('is_invoiced', v)} id="inv-check" />
            <Label htmlFor="inv-check" className="text-xs cursor-pointer">Facturado</Label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Folio</Label><Input value={form.invoice_folio} onChange={e => set('invoice_folio', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Tipo</Label>
              <Select value={form.invoice_type} onValueChange={v => set('invoice_type', v)}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="anticipo">Anticipo</SelectItem>
                  <SelectItem value="parcialidad">Parcialidad</SelectItem>
                  <SelectItem value="complemento">Complemento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SortHead({ col, label, sortCol, sortDir, onSort, align = 'left', color = '' }) {
  const active = sortCol === col;
  return (
    <TableHead
      className={`text-[9px] uppercase tracking-widest font-semibold py-2.5 px-2 cursor-pointer select-none hover:bg-slate-100 transition-colors ${color || 'text-slate-400'} ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : active && sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </TableHead>
  );
}
